import { americanToDecimal } from "@/utils/calculators/odds-utils";

const DECIMAL_AUTHORITATIVE_BOOKS = new Set(["boomsports", "chalkboardpicks", "chalkboardssp", "smacktok"]);

export function getBookDecimalOdds({
  book,
  americanOdds,
  decimalOdds,
}: {
  book: string;
  americanOdds: number;
  decimalOdds: number | null | undefined;
}): number | null {
  const validDecimalOdds = decimalOdds != null && Number.isFinite(decimalOdds) && decimalOdds > 0
    ? decimalOdds
    : null;
  if (DECIMAL_AUTHORITATIVE_BOOKS.has(book)) return validDecimalOdds !== null && validDecimalOdds > 1 ? validDecimalOdds : null;
  if (Number.isFinite(americanOdds) && americanOdds !== 0) return americanToDecimal(americanOdds);
  return validDecimalOdds;
}

export function americanOddsToImpliedProbability(
  americanOdds: number | null | undefined
): number | null {
  if (americanOdds == null || !Number.isFinite(americanOdds)) {
    return null;
  }

  if (americanOdds === 0) {
    return null;
  }

  if (americanOdds > 0) {
    return 100 / (americanOdds + 100);
  }

  const absOdds = Math.abs(americanOdds);
  return absOdds / (absOdds + 100);
}

export function impliedProbabilityToAmericanOdds(probability: number): number {
  if (!Number.isFinite(probability) || probability <= 0 || probability >= 1) {
    return 0;
  }

  if (probability >= 0.5) {
    return Math.round(-(probability * 100) / (1 - probability));
  }

  return Math.round((100 * (1 - probability)) / probability);
}

export function clampedImpliedProbabilityToAmericanOdds(probability: number): number {
  if (!Number.isFinite(probability)) {
    return NaN;
  }
  const clamped = Math.min(Math.max(probability, 1e-6), 1 - 1e-6);
  return impliedProbabilityToAmericanOdds(clamped);
}

export function oddsAreBetterForBettor(
  candidate: number | null | undefined,
  baseline: number | null | undefined,
  epsilon = 0.001
): boolean {
  const candidateProb = americanOddsToImpliedProbability(candidate);
  const baselineProb = americanOddsToImpliedProbability(baseline);

  if (candidateProb === null || baselineProb === null) {
    return false;
  }

  return candidateProb < baselineProb - epsilon;
}

export function toFiniteNumber(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}
